import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { ApiError, type HorecaTableDto, type HorecaZoneDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { RasporedTab } from "./RasporedTab";
import { PromocijeTab } from "./PromocijeTab";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Belgrade" });
const monthStart = () => today().slice(0, 8) + "01";

function errMsg(err: unknown, fallback = "Radnja nije uspela.") {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

type Tab = "prostori" | "konobari" | "stanice" | "radno" | "smene" | "promocije" | "raspored";

export function HorecaPodesavanjaPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("prostori");
  const [err, setErr] = useState<string | null>(null);
  const setup = useQuery({ queryKey: ["horeca-setup"], queryFn: () => api.getHorecaSetup(), retry: false });
  const locations = useQuery({ queryKey: ["horeca-locations"], queryFn: () => api.listBusinessLocationOptions(), retry: false });
  const refresh = () => {
    setErr(null);
    qc.invalidateQueries({ queryKey: ["horeca-setup"] });
    qc.invalidateQueries({ queryKey: ["horeca-staff"] });
  };
  const onError = (e: unknown) => setErr(errMsg(e));

  if (setup.isError) {
    if (setup.error instanceof ApiError && setup.error.response.errorCode === "MODULE_NOT_AVAILABLE")
      return <ModuleGateNotice error={setup.error} moduleKey="horeca" />;
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(setup.error, "Učitavanje nije uspelo.")}</p>;
  }
  if (!setup.data) return <Skeleton className="h-40 w-full" />;

  const tabs: [Tab, string][] = [["prostori", "Prostori i stolovi"], ["konobari", "Konobari i reoni"], ["stanice", "Stanice"], ["radno", "Radno vreme"], ["smene", "Smene"], ["promocije", "Promocije"], ["raspored", "Raspored rada"]];
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">HoReCa podešavanje</h1>
        <p className="text-sm text-slate-500">Sve se podešava ovde, a Android kasa i ostali uređaji povlače podešavanja sa servera.</p>
      </div>
      <div className="flex flex-wrap gap-1 rounded-lg border border-border p-0.5 w-fit">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={"rounded-md px-3 py-1.5 text-sm " + (tab === k ? "bg-brand text-white" : "text-slate-600")}>
            {l}
          </button>
        ))}
      </div>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      {tab === "prostori" && (
        <ZonesTab zones={setup.data.zones} locations={locations.data ?? []} refresh={refresh} onError={onError} />
      )}
      {tab === "konobari" && <StaffTab zones={setup.data.zones} onError={onError} />}
      {tab === "stanice" && <StationsTab stations={setup.data.stations} locations={locations.data ?? []} refresh={refresh} onError={onError} />}
      {tab === "radno" && <WorkTab onError={onError} />}
      {tab === "smene" && <ShiftsTab />}
      {tab === "promocije" && <PromocijeTab />}
      {tab === "raspored" && <RasporedTab />}
    </div>
  );
}

function ZonesTab({ zones, locations, refresh, onError }: { zones: HorecaZoneDto[]; locations: { id: string; name: string }[]; refresh: () => void; onError: (e: unknown) => void }) {
  const [name, setName] = useState("");
  const [loc, setLoc] = useState("");
  const locId = loc || locations[0]?.id || "";
  const addZone = useMutation({
    mutationFn: () => api.createHorecaZone({ business_location_id: locId, name }),
    onSuccess: () => { setName(""); refresh(); },
    onError,
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-surface p-3 shadow-subtle">
        <label className="text-xs text-slate-500">
          Novi prostor (sala, terasa, bašta, sprat…)
          <input className={inputCls + " w-64"} value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Terasa" />
        </label>
        {locations.length > 1 && (
          <label className="text-xs text-slate-500">
            Poslovni prostor
            <select className={inputCls} value={locId} onChange={(e) => setLoc(e.target.value)}>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
        )}
        <Button disabled={!name.trim() || !locId || addZone.isPending} onClick={() => addZone.mutate()}>
          <Plus className="mr-1 h-4 w-4" aria-hidden /> Dodaj prostor
        </Button>
      </div>
      {zones.length === 0 && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">Još nema prostora. Dodajte prvi (npr. Sala).</p>}
      {zones.map((z) => <ZoneCard key={z.id} zone={z} refresh={refresh} onError={onError} />)}
    </div>
  );
}

function ZoneCard({ zone, refresh, onError }: { zone: HorecaZoneDto; refresh: () => void; onError: (e: unknown) => void }) {
  const [label, setLabel] = useState("");
  const [seats, setSeats] = useState(4);
  const [pos, setPos] = useState<Record<string, { x: number; y: number }>>({});
  const drag = useRef<{ id: string; dx: number; dy: number } | null>(null);
  const [editing, setEditing] = useState<HorecaTableDto | null>(null);

  const rename = useMutation({ mutationFn: (n: string) => api.updateHorecaZone(zone.id, { name: n }), onSuccess: refresh, onError });
  const toggle = useMutation({ mutationFn: () => api.updateHorecaZone(zone.id, { is_active: !zone.is_active }), onSuccess: refresh, onError });
  const del = useMutation({ mutationFn: () => api.deleteHorecaZone(zone.id), onSuccess: refresh, onError });
  const add = useMutation({
    mutationFn: () => api.createHorecaTable({ zone_id: zone.id, label, seats }),
    onSuccess: () => { setLabel(""); refresh(); },
    onError,
  });
  const upd = useMutation({
    mutationFn: (t: HorecaTableDto) => api.updateHorecaTable(t.id, { label: t.label, seats: t.seats, shape: t.shape, is_active: t.is_active }),
    onSuccess: () => { setEditing(null); refresh(); },
    onError,
  });
  const delT = useMutation({ mutationFn: (id: string) => api.deleteHorecaTable(id), onSuccess: () => { setEditing(null); refresh(); }, onError });
  const layout = useMutation({ mutationFn: (t: { id: string; x: number; y: number }) => api.saveHorecaLayout([t]), onSuccess: refresh, onError });

  const where = (t: HorecaTableDto) => pos[t.id] ?? { x: t.x, y: t.y };
  const down = (e: React.PointerEvent<HTMLDivElement>, t: HorecaTableDto) => {
    const p = where(t);
    drag.current = { id: t.id, dx: e.clientX - p.x, dy: e.clientY - p.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setPos((s) => ({ ...s, [d.id]: { x: Math.max(0, Math.round(e.clientX - d.dx)), y: Math.max(0, Math.round(e.clientY - d.dy)) } }));
  };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    const np = d ? pos[d.id] : undefined;
    if (d && np) layout.mutate({ id: d.id, x: np.x, y: np.y });
  };

  return (
    <div className={"rounded-xl border border-border bg-surface p-3 shadow-subtle " + (zone.is_active ? "" : "opacity-60")}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <input
          className="h-9 rounded-lg border border-transparent px-2 text-base font-semibold text-slate-900 hover:border-border focus:border-brand focus:outline-none"
          defaultValue={zone.name}
          onBlur={(e) => e.target.value.trim() && e.target.value !== zone.name && rename.mutate(e.target.value)}
        />
        <div className="flex items-center gap-1 text-xs">
          <span className="text-slate-500">{zone.tables.length} stolova</span>
          <button className="rounded px-2 py-1 hover:bg-slate-100" onClick={() => toggle.mutate()}>{zone.is_active ? "Isključi" : "Uključi"}</button>
          <button className="rounded p-1.5 text-red-600 hover:bg-red-50" title="Obriši prostor" onClick={() => window.confirm(`Obrisati prostor „${zone.name}“?`) && del.mutate()}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="mb-2 text-xs text-slate-500">Prevucite stolove da složite raspored sale.</div>
      <div className="relative h-72 overflow-auto rounded-lg border border-dashed border-border bg-slate-50 touch-none">
        {zone.tables.map((t) => {
          const p = where(t);
          return (
            <div
              key={t.id}
              onPointerDown={(e) => down(e, t)}
              onPointerMove={move}
              onPointerUp={up}
              onDoubleClick={() => setEditing(t)}
              style={{ left: p.x, top: p.y, width: t.shape === "rect" ? 108 : 72, height: 72 }}
              className={"absolute flex cursor-grab select-none flex-col items-center justify-center border-2 border-brand/60 bg-white text-sm shadow-sm active:cursor-grabbing " + (t.shape === "round" ? "rounded-full" : "rounded-lg") + (t.is_active ? "" : " opacity-50")}
              title="Dupli klik za izmenu"
            >
              <span className="font-semibold">{t.label}</span>
              <span className="text-[11px] text-slate-500">{t.seats} mesta</span>
            </div>
          );
        })}
        {zone.tables.length === 0 && <p className="p-4 text-sm text-slate-400">Nema stolova. Dodajte ih ispod.</p>}
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-500">Oznaka stola<input className={inputCls + " w-32"} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="npr. 1 ili T3" /></label>
        <label className="text-xs text-slate-500">Mesta<input className={inputCls + " w-20"} type="number" min={1} max={100} value={seats} onChange={(e) => setSeats(Math.max(1, Number(e.target.value) || 1))} /></label>
        <Button disabled={!label.trim() || add.isPending} onClick={() => add.mutate()}>Dodaj sto</Button>
      </div>

      {editing && (
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-brand/40 bg-brand/5 p-3">
          <label className="text-xs text-slate-500">Oznaka<input className={inputCls + " w-32"} value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Mesta<input className={inputCls + " w-20"} type="number" min={1} value={editing.seats} onChange={(e) => setEditing({ ...editing, seats: Math.max(1, Number(e.target.value) || 1) })} /></label>
          <label className="text-xs text-slate-500">Oblik
            <select className={inputCls} value={editing.shape} onChange={(e) => setEditing({ ...editing, shape: e.target.value })}>
              <option value="square">Kvadratni</option><option value="round">Okrugli</option><option value="rect">Pravougaoni</option>
            </select>
          </label>
          <label className="flex items-center gap-1 pb-2 text-xs"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Aktivan</label>
          <Button onClick={() => upd.mutate(editing)}>Sačuvaj</Button>
          <Button variant="secondary" onClick={() => setEditing(null)}>Otkaži</Button>
          <button className="pb-2 text-xs text-red-600" onClick={() => window.confirm("Obrisati sto?") && delT.mutate(editing.id)}>Obriši sto</button>
        </div>
      )}
    </div>
  );
}

function StaffTab({ zones, onError }: { zones: HorecaZoneDto[]; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const staff = useQuery({ queryKey: ["horeca-staff"], queryFn: () => api.getHorecaStaff(), retry: false });
  const save = useMutation({
    mutationFn: (v: { userId: string; see_all: boolean; zone_ids: string[] }) => api.saveHorecaStaff(v.userId, { see_all: v.see_all, zone_ids: v.zone_ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["horeca-staff"] }),
    onError,
  });
  if (!staff.data) return <Skeleton className="h-24 w-full" />;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Svakom konobaru odredite koje prostore opslužuje. Konobar vidi samo svoj reon, osim ako je označeno „Vidi sve“.</p>
      {staff.data.map((s) => (
        <div key={s.user_id} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-medium">{s.name} <span className="font-normal text-slate-500">· {s.role}</span></div>
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={s.see_all} onChange={(e) => save.mutate({ userId: s.user_id, see_all: e.target.checked, zone_ids: s.zone_ids })} /> Vidi sve
            </label>
          </div>
          {!s.see_all && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {zones.map((z) => (
                <label key={z.id} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={s.zone_ids.includes(z.id)}
                    onChange={(e) =>
                      save.mutate({ userId: s.user_id, see_all: false, zone_ids: e.target.checked ? [...s.zone_ids, z.id] : s.zone_ids.filter((x) => x !== z.id) })
                    }
                  />
                  {z.name}
                </label>
              ))}
              {zones.length === 0 && <span className="text-sm text-slate-400">Prvo dodajte prostore.</span>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StationsTab({ stations, locations, refresh, onError }: { stations: { id: string; name: string; category_ids: string[]; is_active: boolean; output_mode: string; printer_name: string | null }[]; locations: { id: string; name: string }[]; refresh: () => void; onError: (e: unknown) => void }) {
  const [name, setName] = useState("");
  const cats = useQuery({ queryKey: ["horeca-categories"], queryFn: () => api.listProductCategories(), retry: false });
  const add = useMutation({ mutationFn: () => api.createHorecaStation({ name, business_location_id: locations.length === 1 ? (locations[0]?.id ?? null) : null }), onSuccess: () => { setName(""); refresh(); }, onError });
  const upd = useMutation({ mutationFn: (v: { id: string; category_ids?: string[]; is_active?: boolean; output_mode?: string; printer_name?: string | null }) => api.updateHorecaStation(v.id, v), onSuccess: refresh, onError });
  const del = useMutation({ mutationFn: (id: string) => api.deleteHorecaStation(id), onSuccess: refresh, onError });
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Stanica je mesto gde se priprema (kuhinja, šank…). Kategorije artikala koje izaberete idu na njen ekran.</p>
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-surface p-3 shadow-subtle">
        <label className="text-xs text-slate-500">Nova stanica<input className={inputCls + " w-64"} value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Kuhinja" /></label>
        <Button disabled={!name.trim() || add.isPending} onClick={() => add.mutate()}><Plus className="mr-1 h-4 w-4" aria-hidden /> Dodaj stanicu</Button>
      </div>
      {stations.map((s) => (
        <div key={s.id} className={"rounded-xl border border-border bg-surface p-3 shadow-subtle " + (s.is_active ? "" : "opacity-60")}>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold">{s.name}</span>
            <div className="flex items-center gap-1 text-xs">
              <button className="rounded px-2 py-1 hover:bg-slate-100" onClick={() => upd.mutate({ id: s.id, is_active: !s.is_active })}>{s.is_active ? "Isključi" : "Uključi"}</button>
              <button className="rounded p-1.5 text-red-600 hover:bg-red-50" onClick={() => window.confirm(`Obrisati stanicu „${s.name}“?`) && del.mutate(s.id)}><Trash2 className="h-4 w-4" aria-hidden /></button>
            </div>
          </div>
          <div className="mb-2 flex flex-wrap items-end gap-3">
            <label className="text-xs text-slate-500">Gde se prikazuje
              <select className={inputCls + " block w-56"} value={s.output_mode} onChange={(e) => upd.mutate({ id: s.id, output_mode: e.target.value })}>
                <option value="SCREEN">Ekran (web, Android, desktop)</option>
                <option value="PRINTER">Samo štampač</option>
                <option value="BOTH">Ekran i štampač</option>
              </select>
            </label>
            {s.output_mode !== "SCREEN" && (
              <label className="text-xs text-slate-500">Naziv štampača
                <input className={inputCls + " block w-56"} defaultValue={s.printer_name ?? ""} placeholder="npr. Kuhinja 80mm" onBlur={(e) => e.target.value !== (s.printer_name ?? "") && upd.mutate({ id: s.id, printer_name: e.target.value })} />
              </label>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(cats.data ?? []).map((c) => (
              <label key={c.id} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={s.category_ids.includes(c.id)}
                  onChange={(e) => upd.mutate({ id: s.id, category_ids: e.target.checked ? [...s.category_ids, c.id] : s.category_ids.filter((x) => x !== c.id) })}
                />
                {c.name}
              </label>
            ))}
            {cats.data?.length === 0 && <span className="text-sm text-slate-400">Nema kategorija artikala.</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function WorkTab({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const cur = useQuery({ queryKey: ["horeca-work-current"], queryFn: () => api.getHorecaWorkCurrent(), retry: false });
  const rep = useQuery({ queryKey: ["horeca-work-report", from, to], queryFn: () => api.getHorecaWorkReport({ from, to }), retry: false });
  const done = () => { qc.invalidateQueries({ queryKey: ["horeca-work-current"] }); qc.invalidateQueries({ queryKey: ["horeca-work-report"] }); };
  const inn = useMutation({ mutationFn: () => api.horecaClockIn(), onSuccess: done, onError });
  const out = useMutation({ mutationFn: () => api.horecaClockOut(), onSuccess: done, onError });
  const hm = (m: number) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}min`;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface p-3 shadow-subtle">
        {cur.data?.session ? (
          <>
            <span className="text-sm">Prijavljeni ste od {new Date(cur.data.session.clock_in_at).toLocaleTimeString("sr-RS", { hour: "2-digit", minute: "2-digit" })}</span>
            <Button onClick={() => out.mutate()} disabled={out.isPending}>Odjavi me</Button>
          </>
        ) : (
          <>
            <span className="text-sm text-slate-600">Niste prijavljeni na rad.</span>
            <Button onClick={() => inn.mutate()} disabled={inn.isPending}>Prijavi me</Button>
          </>
        )}
        <span className="text-xs text-slate-400">Kasa i telefon konobara isto prijavljuju radnike.</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500">Od<input className={inputCls + " block"} type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-xs text-slate-500">Do<input className={inputCls + " block"} type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </div>
      {rep.isLoading && <Skeleton className="h-20 w-full" />}
      {rep.data?.workers.length === 0 && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">Nema evidentiranog rada u periodu.</p>}
      {rep.data?.workers.map((w) => (
        <div key={w.user_id} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
          <div className="flex justify-between text-sm font-medium"><span>{w.name ?? "—"}</span><span>{w.total_hours.toLocaleString("sr-RS")} h</span></div>
          <div className="mt-1 space-y-0.5 text-xs text-slate-600">
            {w.sessions.map((s) => (
              <div key={s.id}>
                {new Date(s.clock_in_at).toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                {" – "}
                {s.clock_out_at ? new Date(s.clock_out_at).toLocaleTimeString("sr-RS", { hour: "2-digit", minute: "2-digit" }) : "u toku"}
                {" · "}{hm(s.minutes)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const money = (n: number) => n.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function ShiftsTab() {
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["horeca-shifts"], queryFn: () => api.listPosShifts(), retry: false });
  const rep = useQuery({ queryKey: ["horeca-shift-report", open], queryFn: () => api.getPosShiftReport(open as string), enabled: !!open, retry: false });
  if (list.isLoading) return <Skeleton className="h-20 w-full" />;
  if (list.isError) return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Učitavanje smena nije uspelo.</p>;
  const rows = list.data?.shifts ?? [];
  if (!rows.length) return <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">Još nema smena. Smenu otvara kasa na uređaju.</p>;
  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-500">Smene otvara i zatvara kasa. Ovde vidite ko je radio, koliko je prodato i da li gotovina odgovara.</p>
      {rows.map((s) => (
        <div key={s.id} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
          <button className="flex w-full flex-wrap items-center justify-between gap-2 text-left" onClick={() => setOpen(open === s.id ? null : s.id)}>
            <span className="text-sm font-semibold">Smena {s.number} · {s.openedBy || "—"}</span>
            <span className="text-xs text-slate-500">{when(s.openedAt)} – {s.status === "OPEN" ? "otvorena" : when(s.closedAt)}{s.autoClosed ? " (automatski zatvorena)" : ""}</span>
          </button>
          {open === s.id && (
            <div className="mt-3 border-t border-border pt-3 text-sm">
              {!rep.data && <Skeleton className="h-16 w-full" />}
              {rep.data && (
                <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
                  <div className="flex justify-between"><span>Prodaja ({rep.data.sales.count})</span><span>{money(rep.data.sales.gross)}</span></div>
                  <div className="flex justify-between"><span>Povrati ({rep.data.refunds.count})</span><span>{money(rep.data.refunds.gross)}</span></div>
                  <div className="flex justify-between font-medium"><span>Neto</span><span>{money(rep.data.net.gross)}</span></div>
                  <div className="flex justify-between"><span>Početna gotovina</span><span>{money(rep.data.cash.opening)}</span></div>
                  <div className="flex justify-between"><span>Očekivano u kasi</span><span>{money(rep.data.cash.expected)}</span></div>
                  <div className="flex justify-between"><span>Prebrojano</span><span>{rep.data.cash.counted === null ? "—" : money(rep.data.cash.counted)}</span></div>
                  {rep.data.cash.difference !== null && (
                    <div className={"flex justify-between font-medium " + (Math.abs(rep.data.cash.difference) >= 0.01 ? "text-red-600" : "text-emerald-700")}>
                      <span>Razlika</span><span>{money(rep.data.cash.difference)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
